import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarCheck, ArrowRight, ShieldCheck, Users, GraduationCap, Clock, ExternalLink, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Eyebrow, PrimaryButton, GoldButton } from "@/components/Ui";
import CosenzaCard from "@/components/CosenzaCard";
import { ACTIVITIES, SERVICES, POLI, TESTIMONIALS, FAQ, IMAGES, CONFIG } from "@/data/content";

const fade = { hidden: { opacity: 0, y: 18 }, show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.05 } }) };

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-navy-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Personale scolastico" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/60" />
          <div className="absolute inset-0 noise-overlay opacity-[0.05]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 md:py-36">
          <motion.div initial="hidden" animate="show" variants={fade} className="max-w-3xl">
            <Eyebrow light>Palumbo Academy · Sindacato Scuola</Eyebrow>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05]">
              Al fianco del personale scolastico, ogni giorno.
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
              Consulenza sindacale, assistenza nelle procedure scolastiche, formazione, CAF e Patronato.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <GoldButton href="https://book.heygoldie.com/PalumboAcademy" data-testid="hero-prenota-btn"><CalendarCheck className="w-4 h-4" /> Prenota un appuntamento</GoldButton>
              <Link to="/servizi" data-testid="hero-servizi-btn" className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold-500 text-white font-bold text-sm uppercase tracking-[0.1em] px-8 py-4 transition-colors duration-200">
                Scopri i nostri servizi <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: Users, t: "Migliaia di assistiti", s: "Docenti, ATA ed educatori" },
            { icon: ShieldCheck, t: "Tutela sindacale", s: "Assistenza e ricorsi" },
            { icon: GraduationCap, t: "Formazione certificata", s: "CFU, TFA, certificazioni" },
            { icon: Clock, t: "3 sedi + online", s: "Cosenza e provincia" },
          ].map((it, i) => (
            <div key={it.t} className="flex items-start gap-4" data-testid={`trust-item-${i}`}>
              <div className="w-11 h-11 bg-navy-900 flex items-center justify-center shrink-0"><it.icon className="w-5 h-5 text-gold-500" /></div>
              <div>
                <div className="font-serif text-lg font-semibold text-navy-900 leading-tight">{it.t}</div>
                <div className="text-sm text-slate-500">{it.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ACTIVITIES */}
      <section className="bg-[#F8F9FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <Eyebrow>Le nostre attività</Eyebrow>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium text-navy-900 tracking-tight">Assistenza completa per ogni procedura scolastica</h2>
            <p className="mt-4 text-slate-600">Un unico punto di riferimento per tutte le principali procedure amministrative, contrattuali e professionali della scuola.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ACTIVITIES.map((a, i) => (
              <motion.div
                key={a.title}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                variants={fade}
                data-testid={`activity-card-${i}`}
                className="group bg-white border border-gray-100 p-6 shadow-crisp hover:shadow-crisp-lg hover:border-gold-500 transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-navy-900 group-hover:bg-gold-500 flex items-center justify-center shrink-0 transition-colors duration-300">
                    <a.icon className="w-6 h-6 text-gold-500 group-hover:text-navy-900 transition-colors duration-300" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-navy-900">{a.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-12">
            <PrimaryButton to="/servizi">Tutti i servizi <ArrowRight className="w-4 h-4" /></PrimaryButton>
          </div>
        </div>
      </section>

      {/* ABOUT SPLIT */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={IMAGES.consulting} alt="Consulenza professionale" className="w-full h-[460px] object-cover" />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-gold-500 text-navy-900 p-6 max-w-xs shadow-crisp-lg">
              <div className="font-serif text-2xl font-semibold">Esperienza consolidata</div>
              <p className="text-sm mt-1 font-medium">nel settore scolastico e sindacale.</p>
            </div>
          </div>
          <div>
            <Eyebrow>Chi siamo</Eyebrow>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium text-navy-900 tracking-tight">Tutela, assistenza e formazione per la scuola</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Palumbo Academy nasce con l'obiettivo di offrire assistenza, tutela e formazione al personale della scuola. Accompagniamo docenti, personale ATA, educatori e aspiranti lavoratori nelle principali procedure amministrative, contrattuali e professionali.
            </p>
            <div className="mt-8 flex items-center gap-4 border-l-2 border-gold-500 pl-5">
              <div>
                <div className="font-serif text-xl font-semibold text-navy-900">Prof. Riccardo Palumbo</div>
                <div className="text-sm text-gold-600 font-semibold uppercase tracking-[0.12em]">Dirigente Sindacale · Segretario Nazionale</div>
              </div>
            </div>
            <div className="mt-8"><PrimaryButton to="/chi-siamo">Scopri di più <ArrowRight className="w-4 h-4" /></PrimaryButton></div>
          </div>
        </div>
      </section>

      {/* POLI DIDATTICI */}
      <section className="bg-navy-900 py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-[0.05]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <Eyebrow light>Poli Didattici & Certificazioni</Eyebrow>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium text-white tracking-tight">Formazione e titoli riconosciuti</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {POLI.map((p, i) => (
              <div key={p.name} data-testid={`polo-card-${i}`} className="border border-white/15 hover:border-gold-500 p-6 transition-colors duration-300 group">
                <p.icon className="w-8 h-8 text-gold-500 mb-4" />
                <div className="font-serif text-lg font-semibold text-white leading-snug">{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#F8F9FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <Eyebrow>Testimonianze</Eyebrow>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium text-navy-900 tracking-tight">La voce dei nostri assistiti</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={t.name} data-testid={`testimonial-${i}`} className="bg-white border border-gray-100 p-6 shadow-crisp flex flex-col">
                <div className="flex gap-1 text-gold-500 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-gold-500" />)}</div>
                <p className="text-slate-600 text-sm leading-relaxed flex-1">"{t.text}"</p>
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="font-serif text-lg font-semibold text-navy-900">{t.name}</div>
                  <div className="text-xs text-gold-600 uppercase tracking-[0.12em] font-semibold">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center">
            <Eyebrow>Domande frequenti</Eyebrow>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium text-navy-900 tracking-tight">Le risposte alle tue domande</h2>
          </div>
          <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
            {FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-gray-200">
                <AccordionTrigger className="text-left font-serif text-lg md:text-xl font-semibold text-navy-900 hover:text-gold-600 py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed text-base pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CosenzaCard />

      {/* CTA */}
      <section className="bg-gold-500 py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-navy-900">Hai bisogno di assistenza?</h2>
            <p className="mt-2 text-navy-900/80 font-medium">Prenota una consulenza o accedi direttamente alla piattaforma GOLDIE.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <PrimaryButton href="https://book.heygoldie.com/PalumboAcademy"><CalendarCheck className="w-4 h-4" /> Prenota appuntamento</PrimaryButton>
            <a href={CONFIG.goldieUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-white hover:bg-navy-900 hover:text-white text-navy-900 font-bold text-sm uppercase tracking-[0.1em] px-8 py-4 transition-colors duration-200" data-testid="goldie-cta-btn">
              Piattaforma GOLDIE <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
